import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  EyeIcon,
  EllipsisVerticalIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CalendarIcon,
  CurrencyDollarIcon,

  TagIcon,
  BellAlertIcon,
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  UserGroupIcon as UserGroupSolidIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { History, BookOpen, Loader2, X, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  fetchDoctorClinicProfile, 
  fetchOnlineAppointments, 
  fetchOfflineAppointments,
  deleteDoctorAccount,
} from '../../../services/MasterAdmin/doctorClinicService';
import { toggleVendorStatus, fetchVendorCommissionRate, updateVendorCommissionRate, fetchVendorAuthActivity, VendorAuthActivity } from '../../../services/MasterAdmin/vendorService';
import { DoctorClinic } from '../../../models/MasterAdmin/doctorClinic';
import { AppointmentResponse, DoctorClinicAppointment } from '../../../models/MasterAdmin/doctorClinicAppointment';
import VendorBankDetailsModal from '../../../components/MasterAdmin/VendorBankDetailsModal';
import DoctorClinicAppointmentDetailsModal from '../../../components/MasterAdmin/DoctorClinicAppointmentDetailsModal';
import { formatDoctorName, formatDoctorSpecialities } from '../../../utils/doctorName';
import { RecipientNotificationsList } from '../../../components/MasterAdmin/RecipientNotificationsList';
import PermissionTable from '../../../components/MasterAdmin/PermissionTable';
import VendorProfileUpdateHistoryPanel from '../../../components/MasterAdmin/VendorProfileUpdateHistoryPanel';
import VendorDoctorBlogsPanel from '../../../components/MasterAdmin/VendorDoctorBlogsPanel';
import VendorAuthActivitySection from '../../../components/MasterAdmin/VendorAuthActivitySection';
import { VendorEventTimelinePanel } from './VendorEventTimelinePage';
import VendorLedgerDetailsPage from '../../Accountant/VendorLedgerDetailsPage';

type DoctorClinicTab =
  | 'online'
  | 'offline'
  | 'notifications'
  | 'permissions'
  | 'profile-updates'
  | 'blogs'
  | 'event-timeline'
  | 'login-activity'
  | 'settlement';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: string;
  description?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, trendValue, description }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {trendValue}
          </p>
        )}
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">{icon}</div>
    </div>
  </div>
);

const APPOINTMENT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'no_call', label: 'No Call' },
  { value: 'no_show', label: 'No Show' },
  { value: 'broadcasted', label: 'Broadcasted' },
  { value: 'awaiting_accept', label: 'Awaiting Accept' },
  { value: 'expired', label: 'Expired' },
  { value: 'converted_to_instant', label: 'Converted To Instant' },
] as const;

const formatStatusLabel = (status: string) =>
  String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const appointmentStatusBadgeClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
    case 'pending':
    case 'awaiting_accept':
    case 'broadcasted':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    case 'cancelled':
    case 'expired':
    case 'no_show':
    case 'no_call':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
    case 'confirmed':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
    case 'postponed':
    case 'rescheduled':
    case 'converted_to_instant':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  }
};

const paymentStatusBadgeClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
    case 'pending':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    case 'failed':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  }
};

const formatAppointmentId = (id: string) => {
  if (!id) return '—';
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
};

const formatOnboardDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const DoctorClinicVendorDashboard: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [clinicData, setClinicData] = useState<DoctorClinic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [isBankDetailsModalOpen, setIsBankDetailsModalOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [commissionRateInput, setCommissionRateInput] = useState<string>('');
  const [referralCommissionInput, setReferralCommissionInput] = useState<string>('');
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountSuccess, setDeleteAccountSuccess] = useState(false);
  const [deletedAccountSummary, setDeletedAccountSummary] = useState<{
    doctorName: string;
    logoutForced: boolean;
    deletedAt?: string;
  } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab: DoctorClinicTab =
    tabFromUrl === 'profile-updates' ||
    tabFromUrl === 'blogs' ||
    tabFromUrl === 'event-timeline' ||
    tabFromUrl === 'login-activity' ||
    tabFromUrl === 'notifications' ||
    tabFromUrl === 'permissions' ||
    tabFromUrl === 'settlement' ||
    tabFromUrl === 'offline' ||
    tabFromUrl === 'online'
      ? tabFromUrl
      : 'online';
  const [activeTab, setActiveTab] = useState<DoctorClinicTab>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [onlineAppointments, setOnlineAppointments] = useState<AppointmentResponse | null>(null);
  const [offlineAppointments, setOfflineAppointments] = useState<AppointmentResponse | null>(null);
  const [authActivity, setAuthActivity] = useState<VendorAuthActivity | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<DoctorClinicAppointment | null>(null);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const itemsPerPage = 20;
  const tabsScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  const updateTabsScrollState = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) {
      setCanScrollTabsLeft(false);
      setCanScrollTabsRight(false);
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollTabsLeft(el.scrollLeft > 4);
    setCanScrollTabsRight(maxScroll > 4 && el.scrollLeft < maxScroll - 4);
  }, []);

  const scrollTabs = (direction: 'left' | 'right') => {
    const el = tabsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -220 : 220, behavior: 'smooth' });
  };

  useEffect(() => {
    updateTabsScrollState();
    const el = tabsScrollRef.current;
    if (!el) return;
    const onScroll = () => updateTabsScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateTabsScrollState);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateTabsScrollState);
    };
  }, [updateTabsScrollState, activeTab]);

  const selectTab = (tab: DoctorClinicTab) => {
    setActiveTab(tab);
    if (tab === 'online') {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ tab }, { replace: true });
    }
  };

  useEffect(() => {
    if (
      tabFromUrl === 'profile-updates' ||
      tabFromUrl === 'blogs' ||
      tabFromUrl === 'event-timeline' ||
      tabFromUrl === 'login-activity' ||
      tabFromUrl === 'notifications' ||
      tabFromUrl === 'permissions' ||
      tabFromUrl === 'settlement' ||
      tabFromUrl === 'offline' ||
      tabFromUrl === 'online'
    ) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  useEffect(() => {
    const loadData = async () => {
      if (!vendorId) return;
      
      try {
        setLoading(true);
        const [clinicResponse, onlineResponse, offlineResponse, authRes] = await Promise.all([
          fetchDoctorClinicProfile(vendorId),
          fetchOnlineAppointments(vendorId),
          fetchOfflineAppointments(vendorId),
          fetchVendorAuthActivity(vendorId).catch(() => null),
        ]);
        
        setClinicData(clinicResponse.data);
        setOnlineAppointments(onlineResponse);
        setOfflineAppointments(offlineResponse);
        if (authRes?.data?.authActivity) {
          setAuthActivity(authRes.data.authActivity);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vendorId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, statusFilter]);

  const openAppointmentDetails = (appointment: DoctorClinicAppointment) => {
    setSelectedAppointment(appointment);
    setIsAppointmentModalOpen(true);
  };

  const closeAppointmentDetails = () => {
    setIsAppointmentModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleToggleStatus = async (vendorId: string, currentStatus: string) => {
    try {
      setUpdatingStatus(vendorId);
      const response = await toggleVendorStatus(vendorId);
      
      if (response.success) {
        setClinicData((prev: DoctorClinic | null) => {
          if (!prev) return null;
          return {
            ...prev,
            vendor: {
              ...prev.vendor,
              status: response.data.status as 'approved' | 'pending' | 'rejected'
            }
          };
        });
      } else {
        setError('Failed to update vendor status. Please try again.');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again later.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const openDeleteAccountModal = () => {
    setDeleteConfirmName('');
    setDeleteReason('');
    setDeleteAccountError(null);
    setDeleteAccountSuccess(false);
    setDeletedAccountSummary(null);
    setIsDeleteAccountModalOpen(true);
    setHeaderMenuOpen(false);
  };

  const closeDeleteAccountModal = () => {
    if (deletingAccount) return;

    const summaryToNavigate = deleteAccountSuccess ? deletedAccountSummary : null;

    setIsDeleteAccountModalOpen(false);
    setDeleteConfirmName('');
    setDeleteReason('');
    setDeleteAccountError(null);
    setDeleteAccountSuccess(false);
    setDeletedAccountSummary(null);

    if (summaryToNavigate) {
      navigate('/vendors', {
        replace: true,
        state: { accountDeletionSuccess: summaryToNavigate },
      });
    }
  };

  const handleConfirmDeleteAccount = async () => {
    if (!vendorId || !clinicData) return;

    const normalizedInput = deleteConfirmName.trim().replace(/\s+/g, ' ').toLowerCase();
    const normalizedDoctor = (clinicData.doctorName || '').trim().replace(/\s+/g, ' ').toLowerCase();

    if (!normalizedInput) {
      setDeleteAccountError('Please enter the doctor name to confirm deletion.');
      return;
    }
    if (normalizedInput !== normalizedDoctor) {
      setDeleteAccountError('Doctor name does not match. Please type the exact name shown above.');
      return;
    }

    try {
      setDeletingAccount(true);
      setDeleteAccountError(null);
      const response = await deleteDoctorAccount(vendorId, {
        confirmDoctorName: deleteConfirmName.trim(),
        reason: deleteReason.trim() || undefined,
      });

      if (response.success) {
        const doctorLabel = formatDoctorName(clinicData.doctorName);
        const summary = {
          doctorName: doctorLabel,
          logoutForced: response.data?.logoutForced ?? true,
          deletedAt: response.data?.deletedAt,
        };

        setDeletedAccountSummary(summary);
        setDeleteAccountSuccess(true);

        toast.success(
          `Account deleted for ${doctorLabel}. Doctor logged out from all active sessions. Appointments and settlements preserved.`,
          { duration: 5000 }
        );
      } else {
        setDeleteAccountError(response.message || 'Failed to delete doctor account.');
      }
    } catch (err: any) {
      setDeleteAccountError(
        err?.response?.data?.message || 'An error occurred while deleting the doctor account.'
      );
    } finally {
      setDeletingAccount(false);
    }
  };

  const deleteNameMatches =
    deleteConfirmName.trim().replace(/\s+/g, ' ').toLowerCase() ===
    (clinicData?.doctorName || '').trim().replace(/\s+/g, ' ').toLowerCase();

  const getCurrentAppointments = () => {
    const appointments = activeTab === 'online' ? onlineAppointments : offlineAppointments;
    return appointments?.data.appointments || [];
  };

  const filteredAppointments = getCurrentAppointments().filter(appointment => {
    const query = searchQuery.trim().toLowerCase();
    const status = String(appointment.status || '').toLowerCase();
    const consultationType = String(appointment.consultationType || '').toLowerCase();
    const matchesSearch =
      !query ||
      (appointment.user?.name || '').toLowerCase().includes(query) ||
      status.includes(query) ||
      consultationType.includes(query) ||
      appointment.clinicAppointmentId.toLowerCase().includes(query);
    const matchesStatus = statusFilter === 'all' || status === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const paginatedAppointments = filteredAppointments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.max(1, Math.ceil(filteredAppointments.length / itemsPerPage));
  const totalFilteredRevenue = filteredAppointments.reduce((sum, apt) => sum + (apt.paidAmount || 0), 0);

  const getCurrentMetrics = () => {
    const appointments = activeTab === 'online' ? onlineAppointments : offlineAppointments;
    return appointments?.data || {
      totalAppointments: 0,
      completed: 0,
      pending: 0,
      cancelled: 0
    };
  };

  const currentMetrics = getCurrentMetrics();
  const openCommissionModal = async () => {
    if (!vendorId) return;
    setIsCommissionModalOpen(true);
    setCommissionError(null);
    setCommissionLoading(true);
    try {
      const response = await fetchVendorCommissionRate(vendorId);
      setCommissionRateInput(String(response?.data?.commissionRate ?? 0));
      setReferralCommissionInput(String(response?.data?.referralCommission ?? 0));
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to load current commission rate');
      setCommissionRateInput('');
      setReferralCommissionInput('');
    } finally {
      setCommissionLoading(false);
    }
  };

  const closeCommissionModal = () => {
    if (commissionSaving) return;
    setIsCommissionModalOpen(false);
    setCommissionError(null);
    setCommissionRateInput('');
    setReferralCommissionInput('');
    setCommissionLoading(false);
  };

  const handleSaveCommissionRate = async () => {
    if (!vendorId || !clinicData) return;
    const parsed = Number(commissionRateInput);
    const referralParsed = Number(referralCommissionInput);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCommissionError('Please enter a valid commission rate between 0 and 100.');
      return;
    }
    if (Number.isNaN(referralParsed) || referralParsed < 0) {
      setCommissionError('Please enter a valid referral commission amount (0 or more).');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to update commission rate to ${parsed.toFixed(2)}% and referral commission to ₹${referralParsed.toFixed(2)} for ${formatDoctorName(clinicData.doctorName)}?`
    );
    if (!confirmed) return;
    try {
      setCommissionSaving(true);
      setCommissionError(null);
      await updateVendorCommissionRate(vendorId, {
        commissionRate: parsed,
        referralCommission: referralParsed,
      });
      setClinicData((prev) => (prev ? { ...prev, commissionRate: parsed } : prev));
      closeCommissionModal();
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to update commission rate');
    } finally {
      setCommissionSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !clinicData) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Clinic data not found'}</p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      {/* Header with Clinic Info */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start space-x-4 min-w-0 flex-1">
            <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-100 dark:ring-blue-900/50 flex items-center justify-center mt-1">
              {clinicData.profilePicture?.trim() ? (
                <img
                  src={clinicData.profilePicture}
                  alt={clinicData.doctorName || 'Doctor'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <UserIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{formatDoctorName(clinicData.doctorName)}</h1>
              {(() => {
                const specialitiesText = formatDoctorSpecialities(clinicData.specializations);
                if (!specialitiesText) return null;
                return (
                  <p className="mt-1 text-sm font-medium text-blue-700 dark:text-blue-300 truncate">
                    {specialitiesText}
                  </p>
                );
              })()}
              <div className="flex flex-col space-y-1.5 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 mt-2">
                <div className="flex items-center text-gray-600 dark:text-gray-400 min-w-0">
                  <MapPinIcon className="h-4 w-4 mr-1 shrink-0" />
                  <span className="text-sm truncate">
                    {[clinicData.address, clinicData.city, clinicData.state].filter(val => val && val.trim() !== '').join(', ') || '—'}
                  </span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400 shrink-0">
                  <PhoneIcon className="h-4 w-4 mr-1 shrink-0" />
                  <span className="text-sm">{clinicData.phoneNumber || '—'}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400 min-w-0">
                  <EnvelopeIcon className="h-4 w-4 mr-1 shrink-0" />
                  <span className="text-sm truncate">{clinicData.email || '—'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center flex-wrap justify-end gap-2 shrink-0">
              <div className="relative flex items-center">
              <button
                onClick={() => setHeaderMenuOpen((o) => !o)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Actions"
                aria-expanded={headerMenuOpen}
                aria-haspopup="true"
              >
                <EllipsisVerticalIcon className="h-6 w-6" />
              </button>
              {headerMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden="true" onClick={() => setHeaderMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 w-52 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        navigate(`/vendors/doctor-clinic/${vendorId}/details`);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Details
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/vendors/doctor-clinic/${vendorId}/timeslots`);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 flex items-center gap-2"
                    >
                      <ClockIcon className="h-4 w-4" />
                      Add Timeslot
                    </button>
                    <button
                      onClick={() => {
                        setIsBankDetailsModalOpen(true);
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
                    >
                      <TagIcon className="h-4 w-4" />
                      Bank Details
                    </button>
                    <button
                      onClick={() => {
                        openCommissionModal();
                        setHeaderMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center gap-2"
                    >
                      <TagIcon className="h-4 w-4" />
                      Commission Rate
                    </button>
                    <button
                      onClick={() => {
                        if (clinicData) {
                          handleToggleStatus(clinicData.vendor.vendorId, clinicData.vendor.status);
                        }
                        setHeaderMenuOpen(false);
                      }}
                      disabled={updatingStatus === clinicData.vendor.vendorId}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        clinicData.vendor.status === 'approved'
                          ? 'text-yellow-700 dark:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                          : 'text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                    >
                      {updatingStatus === clinicData.vendor.vendorId ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : clinicData.vendor.status === 'approved' ? (
                        <>
                          <ClockIcon className="h-4 w-4" />
                          Mark as Pending
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-4 w-4" />
                          Mark as Approve
                        </>
                      )}
                    </button>
                    <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                    <button
                      onClick={openDeleteAccountModal}
                      className="w-full px-4 py-2 text-left text-sm text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete Account
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                clinicData.vendor.status === 'approved'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : clinicData.vendor.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {clinicData.vendor.status.charAt(0).toUpperCase() + clinicData.vendor.status.slice(1)}
                </span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                clinicData.vendor.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {clinicData.vendor.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {authActivity?.recentSessions?.[0]?.appVersion && (
              <div className="flex items-center space-x-2">
                <ComputerDesktopIcon className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">App Version:</span>
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                  v{authActivity.recentSessions[0].appVersion}
                  {authActivity.recentSessions[0].appBuildNumber ? ` (${authActivity.recentSessions[0].appBuildNumber})` : ''}
                </span>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Onboarded:</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {formatOnboardDateTime(clinicData.vendor.createdAt)}
              </span>
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400">Use the three-dots menu for actions</div>
          </div>
        </div>
      </div>

      <VendorBankDetailsModal
        vendorId={vendorId || ''}
        vendorName={formatDoctorName(clinicData?.doctorName)}
        isOpen={isBankDetailsModalOpen}
        onClose={() => setIsBankDetailsModalOpen(false)}
      />
      {isDeleteAccountModalOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-black/50 p-3 sm:p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !deletingAccount) closeDeleteAccountModal();
          }}
        >
          <div className="flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-gray-800 sm:px-6">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">
                  {deleteAccountSuccess ? 'Account Deleted' : 'Delete Doctor Account'}
                </h3>
                {!deleteAccountSuccess && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    This permanently removes personal data for{' '}
                    <span className="font-semibold">{formatDoctorName(clinicData.doctorName)}</span>.
                    The doctor will be logged out immediately.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={closeDeleteAccountModal}
                disabled={deletingAccount}
                className="shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {deleteAccountSuccess ? (
              <>
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-6">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="mt-4 min-w-0 sm:mt-0">
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {formatDoctorName(clinicData.doctorName)}&apos;s account was deleted successfully
                      </p>
                      <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                          <span>Personal profile, documents, timeslots, and blogs were removed</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                          <span>
                            <span className="font-medium text-gray-900 dark:text-white">Logout status:</span>{' '}
                            Doctor logged out from all active sessions and devices immediately
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                          <span>Appointment history and settlement records were preserved</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-5 py-4 dark:border-gray-800 dark:bg-gray-900/40 sm:px-6">
                  <button
                    type="button"
                    onClick={closeDeleteAccountModal}
                    className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-5 p-5 sm:p-6 md:grid-cols-2 md:gap-6">
                  <div className="space-y-4">
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/20">
                      <p className="text-sm font-semibold text-red-800 dark:text-red-200">Data that will be deleted</p>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700 dark:text-red-300">
                        <li>Doctor profile, contact details, address, and clinic photos</li>
                        <li>Documents and certificates</li>
                        <li>Consultation timeslots, blocked dates, and branch listings</li>
                        <li>Speciality fees and commission settings</li>
                        <li>Health blogs authored by this doctor</li>
                        <li>Bank details and policy records</li>
                        <li>Active login sessions (immediate logout)</li>
                      </ul>
                    </div>

                    <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-900/40 dark:bg-green-900/20">
                      <p className="text-sm font-semibold text-green-800 dark:text-green-200">Data that will be preserved</p>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-green-700 dark:text-green-300">
                        <li>Appointment and booking history</li>
                        <li>Settlement, payout, and finance-related records</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
                    <div>
                      <label htmlFor="delete-reason" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Reason for deletion (optional)
                      </label>
                      <textarea
                        id="delete-reason"
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        rows={3}
                        placeholder="e.g. Doctor requested account removal"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        disabled={deletingAccount}
                      />
                    </div>

                    <div>
                      <label htmlFor="delete-confirm-name" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Type <span className="font-semibold">{formatDoctorName(clinicData.doctorName)}</span> to confirm
                      </label>
                      <input
                        id="delete-confirm-name"
                        type="text"
                        value={deleteConfirmName}
                        onChange={(e) => {
                          setDeleteConfirmName(e.target.value);
                          setDeleteAccountError(null);
                        }}
                        placeholder="Enter doctor name exactly"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        disabled={deletingAccount}
                        autoComplete="off"
                      />
                    </div>

                    {deleteAccountError && (
                      <p className="text-sm text-red-600 dark:text-red-400">{deleteAccountError}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4 dark:border-gray-800 dark:bg-gray-900/40 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                  <button
                    type="button"
                    onClick={closeDeleteAccountModal}
                    disabled={deletingAccount}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleConfirmDeleteAccount()}
                    disabled={deletingAccount || !deleteNameMatches}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {deletingAccount && <Loader2 className="h-4 w-4 animate-spin" />}
                    Delete Account
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <DoctorClinicAppointmentDetailsModal
        appointment={selectedAppointment}
        isOpen={isAppointmentModalOpen}
        onClose={closeAppointmentDetails}
      />
      {isCommissionModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Commission Rate</h3>
              <button onClick={closeCommissionModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">{formatDoctorName(clinicData.doctorName)}</p>
              {commissionLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading current rate...</p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Commission Rate (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={commissionRateInput}
                      onChange={(e) => setCommissionRateInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Referral Commission Amount (INR)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={referralCommissionInput}
                      onChange={(e) => setReferralCommissionInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </>
              )}
              {commissionError && <p className="text-sm text-red-600 dark:text-red-400">{commissionError}</p>}
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeCommissionModal}
                disabled={commissionSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCommissionRate}
                disabled={commissionLoading || commissionSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
              >
                {commissionSaving ? 'Saving...' : 'Add / Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consultation Type Tabs — single scrollable row */}
      <div className="mb-8">
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => scrollTabs('left')}
            disabled={!canScrollTabsLeft}
            aria-label="Scroll tabs backward"
            className="shrink-0 rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 disabled:pointer-events-none disabled:opacity-30 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <div
            ref={tabsScrollRef}
            className="flex min-w-0 flex-1 flex-nowrap items-stretch gap-0 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <button
              type="button"
              onClick={() => selectTab('online')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'online'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <ComputerDesktopIcon className="mr-2 h-5 w-5" />
              Online & Instant
            </button>
            <button
              type="button"
              onClick={() => selectTab('offline')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'offline'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <UserGroupSolidIcon className="mr-2 h-5 w-5" />
              Offline
            </button>
            <button
              type="button"
              onClick={() => selectTab('notifications')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'notifications'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <BellAlertIcon className="mr-2 h-5 w-5 text-blue-500" />
              Notifications
            </button>
            <button
              type="button"
              onClick={() => selectTab('permissions')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'permissions'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <ShieldCheckIcon className="mr-2 h-5 w-5 text-indigo-500" />
              Permissions
            </button>
            <button
              type="button"
              onClick={() => selectTab('profile-updates')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'profile-updates'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <History className="mr-2 h-5 w-5 text-primary-600 dark:text-primary-400" />
              Profile Updates
            </button>
            <button
              type="button"
              onClick={() => selectTab('blogs')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'blogs'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <BookOpen className="mr-2 h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              Blogs
            </button>
            <button
              type="button"
              onClick={() => selectTab('event-timeline')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'event-timeline'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <RectangleStackIcon className="mr-2 h-5 w-5 text-teal-600 dark:text-teal-400" />
              Event Timeline
            </button>
            <button
              type="button"
              onClick={() => selectTab('login-activity')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'login-activity'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <ShieldCheckIcon className="mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Login Activity
            </button>
            <button
              type="button"
              onClick={() => selectTab('settlement')}
              className={`inline-flex shrink-0 items-center whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'settlement'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <CurrencyDollarIcon className="mr-2 h-5 w-5 text-green-600 dark:text-green-400" />
              Settlement
            </button>
          </div>
          <button
            type="button"
            onClick={() => scrollTabs('right')}
            disabled={!canScrollTabsRight}
            aria-label="Scroll tabs forward"
            className="shrink-0 rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 disabled:pointer-events-none disabled:opacity-30 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Metrics & Bookings OR Notifications Section */}
      {activeTab === 'notifications' ? (
        <div className="mb-8">
          <RecipientNotificationsList
            recipientType="vendor"
            recipientId={vendorId || ''}
            recipientName={formatDoctorName(clinicData?.doctorName)}
          />
        </div>
      ) : activeTab === 'permissions' ? (
        <div className="mb-8">
          <PermissionTable
            type="vendor"
            id={vendorId}
          />
        </div>
      ) : activeTab === 'profile-updates' ? (
        <div className="mb-8">
          <VendorProfileUpdateHistoryPanel
            vendorId={vendorId || ''}
            vendorName={formatDoctorName(clinicData?.doctorName)}
          />
        </div>
      ) : activeTab === 'blogs' ? (
        <div className="mb-8">
          <VendorDoctorBlogsPanel
            vendorId={vendorId || ''}
            doctorName={formatDoctorName(clinicData?.doctorName)}
          />
        </div>
      ) : activeTab === 'event-timeline' ? (
        <div className="mb-8">
          <VendorEventTimelinePanel
            vendorId={vendorId || ''}
            kindLabel="Doctor / clinic"
          />
        </div>
      ) : activeTab === 'login-activity' ? (
        <div className="mb-8">
          <VendorAuthActivitySection vendorId={vendorId} showSectionTitle />
        </div>
      ) : activeTab === 'settlement' ? (
        <div className="mb-8">
          <VendorLedgerDetailsPage vendorIdProp={vendorId || ''} embedded />
        </div>
      ) : (
        <>
          {/* Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <MetricCard
          title={`Total ${activeTab === 'online' ? 'Online & Instant' : 'Offline'} Bookings`}
          value={currentMetrics.totalAppointments}
          icon={<CalendarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
        />
        <MetricCard
          title="Completed"
          value={currentMetrics.completed}
          icon={<CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
        />
        <MetricCard
          title="Pending"
          value={currentMetrics.pending}
          icon={<ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
        />
        <MetricCard
          title="Cancelled"
          value={currentMetrics.cancelled}
          icon={<XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />}
        />
        <MetricCard
          title="Total Revenue"
          value={`₹${totalFilteredRevenue.toLocaleString('en-IN')}`}
          icon={<CurrencyDollarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />}
          description="From filtered bookings"
        />
      </div>

      {/* Bookings Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 space-y-4 md:space-y-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {activeTab === 'online' ? 'Online & Instant' : 'Offline'} Bookings
          </h2>
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search bookings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg w-full md:w-64 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            >
              {APPOINTMENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {paginatedAppointments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-600 px-4 py-10 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">No bookings found for the selected filters.</p>
          </div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="space-y-3 md:hidden">
              {paginatedAppointments.map((appointment) => (
                <div
                  key={appointment.clinicAppointmentId}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-gray-500 dark:text-gray-400" title={appointment.clinicAppointmentId}>
                        {formatAppointmentId(appointment.clinicAppointmentId)}
                      </p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{appointment.user?.name || 'Unknown Patient'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{appointment.user?.phone_number || '—'}</p>
                    </div>
                    <span className={`shrink-0 px-2 py-1 text-xs font-medium rounded-full ${appointmentStatusBadgeClass(appointment.status)}`}>
                      {formatStatusLabel(appointment.status)}
                    </span>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-xs text-gray-500 dark:text-gray-400">Date</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">{appointment.date}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500 dark:text-gray-400">Time</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">{appointment.time}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500 dark:text-gray-400">Payment</dt>
                      <dd className="mt-0.5">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${paymentStatusBadgeClass(appointment.paymentStatus)}`}>
                          {appointment.paymentStatus.charAt(0).toUpperCase() + appointment.paymentStatus.slice(1)}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500 dark:text-gray-400">Amount</dt>
                      <dd className="mt-0.5 font-medium text-gray-900 dark:text-white">₹{appointment.paidAmount.toLocaleString('en-IN')}</dd>
                    </div>
                  </dl>
                  <button
                    type="button"
                    onClick={() => openAppointmentDetails(appointment)}
                    className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View Details
                  </button>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="ma-table-scroll scrollbar-light hidden md:block -mx-6">
              <table className="w-full min-w-[52rem]">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Appointment ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Patient
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Date &amp; Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 lg:px-6">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedAppointments.map((appointment) => (
                    <tr
                      key={appointment.clinicAppointmentId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                        <span
                          className="font-mono text-xs text-gray-600 dark:text-gray-300"
                          title={appointment.clinicAppointmentId}
                        >
                          {formatAppointmentId(appointment.clinicAppointmentId)}
                        </span>
                      </td>
                      <td className="px-4 py-4 lg:px-6">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{appointment.user?.name || 'Unknown Patient'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{appointment.user?.phone_number || '—'}</div>
                        <div className="max-w-[12rem] truncate text-xs text-gray-500 dark:text-gray-400" title={appointment.user?.emailId}>
                          {appointment.user?.emailId || '—'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-900 dark:text-white lg:px-6">
                        <div>{appointment.date}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{appointment.time}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${appointmentStatusBadgeClass(appointment.status)}`}>
                          {formatStatusLabel(appointment.status)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${paymentStatusBadgeClass(appointment.paymentStatus)}`}>
                          {appointment.paymentStatus.charAt(0).toUpperCase() + appointment.paymentStatus.slice(1)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900 dark:text-white lg:px-6">
                        ₹{appointment.paidAmount.toLocaleString('en-IN')}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 lg:px-6">
                        <button
                          type="button"
                          onClick={() => openAppointmentDetails(appointment)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                        >
                          <EyeIcon className="h-4 w-4" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

        </>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* System Reminders */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">System Reminders</h2>
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                <div className="flex items-start">
                  <BellAlertIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Update Required
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                      Please update your clinic details and consultation timings for Q2 2024
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                      Due by: March 31, 2024
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorClinicVendorDashboard; 